import React from 'react'
import { Show, TextField, DateField, EditButton } from '@refinedev/antd'
import { useShow, useOne } from '@refinedev/core'
import { Typography, Tag } from 'antd'
import type { Tables } from '@muaa/shared'

type Donation = Tables<'donations'>
type Profile = Tables<'profiles'>

const { Title } = Typography

// Real donation statuses (webhook-owned lifecycle + mock-pay seed):
// pending · succeeded · failed · canceled · mock_complete.
const STATUS_COLORS: Record<string, string> = {
  succeeded: 'green',
  pending: 'gold',
  failed: 'red',
  canceled: 'default',
  mock_complete: 'green',
}

// Payment providers: Stripe (in-app), Cheddar Up (hosted page + webhook), mock (seed).
const PROVIDER_LABELS: Record<string, string> = {
  stripe: 'Stripe',
  cheddarup: 'Cheddar Up',
  mock: 'Mock',
}
const PROVIDER_COLORS: Record<string, string> = {
  stripe: 'blue',
  cheddarup: 'gold',
  mock: 'default',
}

const formatMoney = (value: number | undefined) =>
  value != null
    ? `$${Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : '—'

export function DonationShow() {
  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx): a single-record
  // subscription, so a status the webhook settles (pending → succeeded) lands
  // here without a reload. Same shape as events/show.tsx.
  const { query } = useShow<Donation>({ liveMode: 'auto' })
  const isLoading = query.isLoading
  const record = query.data?.data

  // Resolve user_id -> profile name (mirrors rsvps/show pattern)
  const { query: profileQuery } = useOne<Profile>({
    resource: 'profiles',
    id: record?.user_id ?? '',
    meta: { idColumnName: 'user_id' },
    queryOptions: { enabled: !!record?.user_id },
  })

  return (
    <Show isLoading={isLoading} headerButtons={({ defaultButtons }) => <>{defaultButtons}<EditButton /></>}>
      <Title level={5}>Donor</Title>
      <TextField
        value={
          profileQuery.data?.data?.name ??
          record?.donor_name ??
          record?.donor_email ??
          record?.user_id ??
          '—'
        }
      />

      <Title level={5}>Provider</Title>
      {record?.provider ? (
        <Tag color={PROVIDER_COLORS[record.provider] ?? 'default'}>
          {PROVIDER_LABELS[record.provider] ?? record.provider}
        </Tag>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Donor Email</Title>
      {record?.donor_email ? (
        <Typography.Text copyable>{record.donor_email}</Typography.Text>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Fund</Title>
      <TextField value={record?.fund ?? '—'} />

      <Title level={5}>Amount</Title>
      <TextField value={formatMoney(record?.amount)} />

      <Title level={5}>Frequency</Title>
      <TextField value={record?.frequency ?? '—'} />

      <Title level={5}>Status</Title>
      {record?.status ? (
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>{record.status}</Tag>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Stripe Payment Intent</Title>
      {record?.stripe_payment_intent_id ? (
        <Typography.Text copyable code>
          {record.stripe_payment_intent_id}
        </Typography.Text>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>External ID</Title>
      {record?.external_id ? (
        <Typography.Text copyable code>
          {record.external_id}
        </Typography.Text>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Campaign ID</Title>
      <TextField value={record?.campaign_id ?? '—'} />

      <Title level={5}>Created</Title>
      {record?.created_at ? (
        <DateField value={record.created_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}
    </Show>
  )
}
